import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  type ICognitoUserPoolData,
} from 'amazon-cognito-identity-js';

const poolData: ICognitoUserPoolData = {
  UserPoolId: import.meta.env.VITE_USER_POOL_ID,
  ClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
};

const userPool = new CognitoUserPool(poolData);

export interface AuthTokens {
  idToken: string;
  accessToken: string;
}

export interface NewPasswordRequiredResult {
  status: 'NEW_PASSWORD_REQUIRED';
  completeNewPassword: (newPassword: string) => Promise<AuthTokens>;
}

export interface SuccessResult {
  status: 'SUCCESS';
  tokens: AuthTokens;
}

/**
 * v0.4 FR-91 (ADR-0003 amendment): the account is in Cognito's
 * `RESET_REQUIRED` state (after an admin-initiated `AdminResetUserPassword`,
 * v0.3 FR-76 for Admins / the existing Chair reset) -- a *different* SRP
 * signal from `NEW_PASSWORD_REQUIRED` (an `AdminCreateUser` invite). The
 * caller should route into the existing code+new-password
 * (`confirmForgotPassword`) form; a reset code was already emailed, so no
 * new `forgotPassword()` call is needed.
 */
export interface ResetRequiredResult {
  status: 'RESET_REQUIRED';
}

export type LoginResult = SuccessResult | NewPasswordRequiredResult | ResetRequiredResult;

function tokensFromSession(session: CognitoUserSession): AuthTokens {
  return {
    idToken: session.getIdToken().getJwtToken(),
    accessToken: session.getAccessToken().getJwtToken(),
  };
}

/** SRP login against Cognito directly (ADR-0003) -- no Hosted UI. */
export function login(email: string, password: string): Promise<LoginResult> {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  const authDetails = new AuthenticationDetails({ Username: email, Password: password });

  return new Promise((resolve, reject) => {
    user.authenticateUser(authDetails, {
      onSuccess: (session) => resolve({ status: 'SUCCESS', tokens: tokensFromSession(session) }),
      onFailure: (err) => {
        // FR-91: an admin-initiated reset (RESET_REQUIRED) is a distinct
        // signal from the generic auth failure -- route it to the caller so
        // the UI can offer the code+new-password form instead of a bare
        // "login failed" error.
        if (err instanceof Error && err.name === 'PasswordResetRequiredException') {
          resolve({ status: 'RESET_REQUIRED' });
          return;
        }
        reject(err);
      },
      newPasswordRequired: () => {
        resolve({
          status: 'NEW_PASSWORD_REQUIRED',
          completeNewPassword: (newPassword: string) =>
            new Promise<AuthTokens>((res, rej) => {
              user.completeNewPasswordChallenge(
                newPassword,
                {},
                {
                  onSuccess: (session) => res(tokensFromSession(session)),
                  onFailure: (err) => rej(err),
                },
              );
            }),
        });
      },
    });
  });
}

export function forgotPassword(email: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

export function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

/**
 * Ends the Cognito session **in this browser only** (no `GlobalSignOut`, no
 * `RevokeToken` -- ADR-0003/ADR-0004, v0.10 amendments).
 *
 * Two steps, both required:
 *  1. `CognitoUser.signOut()` (no revoke callback) for the *current* user, so
 *     the SDK drops its own in-memory session as well as the cached tokens.
 *  2. A sweep of every remaining key under this client's storage prefix.
 *
 * Step 2 exists because step 1 is **not** prefix-wide (security review M1,
 * verified in `amazon-cognito-identity-js@6.3.20`): `getCurrentUser()`
 * reconstructs the user from `LastAuthUser` and `clearCachedTokens()` removes
 * only keys namespaced by *that* username, while `cacheTokens()` never removes
 * a previous user's. On a shared tablet where two admins have signed in and the
 * first never pressed "Abmelden", the first one's still-valid **refresh token**
 * would therefore survive a kiosk binding -- readable with devtools, and
 * replayable against Cognito for the rest of its 30-day validity. FR-180 says
 * the device retains *no* Cognito session state, so the sweep is the guarantee
 * and the SDK call is the tidy path.
 *
 * `localStorage.clear()` is deliberately NOT used: it would delete
 * `bt.kiosk.deviceToken` and break the bound kiosk (FR-181). The device token
 * lives outside the `CognitoIdentityServiceProvider.*` namespace, so a
 * prefix-scoped sweep cannot touch it.
 */
export function logout(): void {
  userPool.getCurrentUser()?.signOut();
  const prefix = `CognitoIdentityServiceProvider.${poolData.ClientId}.`;
  // Backwards: removeItem() reindexes the store, so a forward loop would skip.
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key?.startsWith(prefix)) localStorage.removeItem(key);
  }
}

/** Returns a valid (refreshed if needed) ID token for the currently signed-in user, if any. */
export function getCurrentIdToken(): Promise<string | undefined> {
  const user = userPool.getCurrentUser();
  if (!user) return Promise.resolve(undefined);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(undefined);
        return;
      }
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

export function getCurrentUserEmail(): string | undefined {
  return userPool.getCurrentUser()?.getUsername();
}
